import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { setFocusedAssetInfo } from 'js/src/data/scenes/scenes-actions';

export interface HtmlDesignTarget {
    sceneId: string;
    assetId: string;
}

/**
 * `activeConversationTarget` is the motion graphics scene currently in edit mode: the scene others
 * dim around, that shows the composition scrubber and its selectable parts.
 */
export interface HtmlDesignState {
    activeConversationTarget: HtmlDesignTarget | null;
    targetByConversation: Record<string, HtmlDesignTarget>;
}

const initialState: HtmlDesignState = {
    activeConversationTarget: null,
    targetByConversation: {},
};

const htmlDesignSlice = createSlice({
    name: 'htmlDesign',
    initialState,
    reducers: {
        setActiveHtmlDesign(state, action: PayloadAction<HtmlDesignTarget>) {
            state.activeConversationTarget = action.payload;
        },
        clearActiveHtmlDesign(state) {
            state.activeConversationTarget = null;
        },
        setHtmlDesignTarget(
            state,
            action: PayloadAction<{ conversationId: string } & HtmlDesignTarget>,
        ) {
            const { conversationId, sceneId, assetId } = action.payload;
            state.targetByConversation[conversationId] = { sceneId, assetId };
        },
        clearHtmlDesignTarget(state, action: PayloadAction<string>) {
            delete state.targetByConversation[action.payload];
        },
    },
    extraReducers: (builder) => {
        // Focusing any luminary asset — a caption, the watermark, a sticker — means the user has moved
        // on to editing that asset, so a codeo scene in edit mode leaves it rather than leaving two
        // editing surfaces open at once.
        builder.addCase(setFocusedAssetInfo, (state) => {
            state.activeConversationTarget = null;
        });
    },
});

export const {
    setActiveHtmlDesign,
    clearActiveHtmlDesign,
    setHtmlDesignTarget,
    clearHtmlDesignTarget,
} = htmlDesignSlice.actions;
export const htmlDesignReducer = htmlDesignSlice.reducer;
